/**
 * UI formatting helpers (tech-debt UX-1 / VC-H).
 *
 * One place for timestamp and count formatting so the tabs render them
 * consistently. The UI review found three different timestamp styles across
 * tabs and a "1 entries" pluralization bug — both come from each call site
 * formatting ad hoc. `lib/` is a leaf module, so any layer may import these.
 */
type TimeInput = number | string | Date;

function toDate(input: TimeInput): Date {
  return input instanceof Date ? input : new Date(input);
}

/** Full local date + time, e.g. "5/29/2026, 7:43:52 PM". For audit/list rows. */
export function formatDateTime(input: TimeInput): string {
  return toDate(input).toLocaleString();
}

/** Local time only, e.g. "7:43:52 PM". For high-frequency event/tick streams. */
export function formatTime(input: TimeInput): string {
  return toDate(input).toLocaleTimeString();
}

/**
 * Count + correctly-singularised noun: `pluralize(1, 'entry', 'entries')` →
 * "1 entry"; `pluralize(3, 'key')` → "3 keys". Pass an explicit plural for
 * irregular words; otherwise an "s" is appended.
 */
export function pluralize(count: number, singular: string, plural = `${singular}s`): string {
  return `${count} ${count === 1 ? singular : plural}`;
}
